/**
 * @fileoverview Cookies Policy page component.
 * @summary Displays RGPD-compliant cookies policy.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/CookiesPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import PageHeader from '../components/PageHeader.js'
import { Link } from 'react-router-dom'

/**
 * @summary Cookies Policy page component.
 * @returns {React.ReactElement} Cookies policy page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function CookiesPage() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-base-50 dark:bg-base-900">
      <PageHeader
        title={t('cookiesPage.title')}
        subtitle={t('cookiesPage.subtitle')}
      />

      <div className="page-container mx-auto py-4 px-4 bg-white dark:bg-gray-800 rounded-lg ">
        <div className="prose dark:prose-invert max-w-none">
          {/* Section 1: Overview */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('cookiesPage.overview')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4 dark:text-white">
              {t('cookiesPage.overviewText')}
            </p>
          </section>

          {/* Section 2: What are cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary)">
              {t('cookiesPage.whatAreCookies')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('cookiesPage.whatAreCookiesText')}
            </p>
          </section>

          {/* Section 3: Types of cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary)">
              {t('cookiesPage.typesOfCookies')}
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-(--color-text-primary) mb-2">
                  {t('cookiesPage.sessionCookies')}
                </h3>
                <p className="text-(--color-text-secondary)">
                  {t('cookiesPage.sessionCookiesText')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-(--color-text-primary) mb-2">
                  {t('cookiesPage.persistentCookies')}
                </h3>
                <p className="text-(--color-text-secondary)">
                  {t('cookiesPage.persistentCookiesText')}
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: How we use cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary)">
              {t('cookiesPage.howWeUseCookies')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary)">
              <li>{t('cookiesPage.useAuthentication')}</li>
              <li>{t('cookiesPage.usePreferences')}</li>
              <li>{t('cookiesPage.useSecurity')}</li>
              <li>{t('cookiesPage.usePerformance')}</li>
            </ul>
          </section>

          {/* Section 5: Your rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary)">
              {t('cookiesPage.yourRights')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('cookiesPage.yourRightsText')}
            </p>
          </section>

          {/* Section 6: Managing cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary)">
              {t('cookiesPage.managingCookies')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('cookiesPage.managingCookiesText')}
            </p>
          </section>

          {/* Section 7: Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary)">
              {t('cookiesPage.contact')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('cookiesPage.contactText')}
            </p>
          </section>

          {/* Footer CTA */}
          <section className="bg-base-100 dark:bg-base-800 p-6 rounded-lg border border-base-200 dark:border-base-700">
            <p className="text-(--color-text-secondary) mb-4">
              {t('cookiesPage.footer')}
            </p>
            <Link to="/" className="btn btn-primary">
              {t('common.backHome')}
            </Link>
          </section>
        </div>
      </div>
    </div>
  )
}

